import { useQuery } from "@tanstack/react-query";

import { type ProductClient } from "@fitness-app/data-models/entities/ProductClient";

import { supabase } from "~/store/initializeStore";

/**
 * All product_client rows the client owns under a given email. Mirrors the backend
 * `getProductClientsForAuthor` lookup so the email-change modal can preview exactly
 * which products the "apply to all" switch will re-point (TRE-1668).
 */
export const useProductClientsByEmail = (email: string, enabled = true) => {
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["productClientsByEmail", email],
    enabled: enabled && Boolean(email),
    queryFn: async ({ signal }) => {
      const builder = supabase
        .from("product_client")
        .select("id, productId, status, product")
        .eq("email", email.toLowerCase())
        .order("createdAt", { ascending: false });
      const query = signal ? builder.abortSignal(signal) : builder;
      const { data, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      return (data ?? []) as unknown as Pick<ProductClient, "id" | "productId" | "status" | "product">[];
    },
  });

  return {
    list: data ?? [],
    isLoading,
    isFetching,
    isError,
  };
};
